<template>
  <div>
    <h5 class="text-center mb-2">Top 10 IPs</h5>
    <table class="table table-striped table-primary table-sm table-bordered">
      <thead>
      <tr>
        <th>IP</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topIps" :key="idx">
        <td>{{ item.ip }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';

const topIps = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Fetches and processes data of links visited by a user.
 * The function retrieves user-specific data from an API endpoint, calculates the most frequently used IPs,
 * and updates a reactive variable with the top 10 IPs and their respective visit counts.
 *
 * Functionality:
 * - Retrieves the user's ID and token from sessionStorage.
 * - Sends a GET request to the API endpoint to retrieve the list of visited links.
 * - Calculates the count of visits for each unique IP address.
 * - Sorts the IP addresses by visit count in descending order.
 * - Updates a reactive variable with the top 10 IPs and their corresponding statistics.
 * - Handles errors by updating an error reactive variable if data loading fails.
 * - Ensures the loading reactive variable reflects the status of the data-fetching process.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const id = sessionStorage.getItem('id');
    const res = await axios.get(`${apiUrl}/linksvisited/id_user/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const ip = item?.ip || 'Unknown';
        counts[ip] = (counts[ip] || 0) + 1;
      });
      topIps.value = Object.entries(counts)
          .map(([ip, visits]) => ({ ip, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load IP data';
  } finally {
    loading.value = false;
  }
});
</script>
